import { useEffect } from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import intl from 'react-intl-universal';
import { useParams } from 'react-router-dom';

import LinearProgress from '@mui/material/LinearProgress';

import { Board } from '@/models/BoardModel';

import {
  selectActiveBoard,
  selectIsLoadingActiveBoard,
  selectIsLoadingRoomMembers,
  selectRoomMembersRequested,
} from '../../ducks/Boards';
import { selectCardsRequested, selectIsLoadingCards } from '../../ducks/Cards';
import { actions as communityActions } from '../../ducks/Communities';
import { actions as roomActions } from '../../ducks/Rooms';
import { alertAction } from '../../thunks/Alerts';
import * as boardsActions from '../../thunks/Boards';
import * as cardsActions from '../../thunks/Cards';

import Error from '../../common/Error';
import { CardCreateContainer as CardCreate, CardUpdateContainer as CardUpdate } from '.';

const mapStateToProps = () =>
  createStructuredSelector({
    board: selectActiveBoard(),
    isLoadingBoard: selectIsLoadingActiveBoard(),
    isLoadingCards: selectIsLoadingCards(),
    cardsRequested: selectCardsRequested(),
    roomMembersRequested: selectRoomMembersRequested(),
    isLoadingRoomMembers: selectIsLoadingRoomMembers(),
  });

const mapDispatchToProps = (dispatch: Dispatch) => ({
  actions: bindActionCreators(
    {
      ...cardsActions,
      ...boardsActions,
      setActiveRoom: roomActions.setActiveRoom,
      setActiveCommunity: communityActions.setActiveCommunity,
    },
    dispatch,
  ),
});

const CardContainer = ({
  board,
  actions,
  isLoadingBoard,
  isLoadingCards,
  cardsRequested,
  roomMembersRequested,
  isLoadingRoomMembers,
}: {
  board: Board;
  isLoadingBoard: boolean;
  isLoadingCards: boolean;
  cardsRequested: { boardId: string };
  roomMembersRequested: { communityId: string; roomId: string };
  isLoadingRoomMembers: boolean;
  actions: any;
}) => {
  const params = useParams();
  const { communityId, roomId, boardId, cardId } = params;

  useEffect(() => {
    if (!board || board.id !== boardId) {
      actions.fetchActiveBoard({ communityId, roomId, boardId }).then((action: any) => {
        const workflowConfig = action.payload.activeBoard.workflow_config;

        actions.setActiveCommunity({ communityId: workflowConfig.community_id });
        actions.setActiveRoom({ roomId: workflowConfig.room_id });

        alertAction({ action, error: intl.get('card.fetch_board.error') });
      });
    }

    if (cardsRequested.boardId != boardId) {
      actions
        .fetchCards({ communityId, roomId, boardId })
        .then((action: any) => alertAction({ action, error: intl.get('card.fetch.error') }));
    }

    if (roomMembersRequested.communityId !== communityId || roomMembersRequested.roomId !== roomId) {
      actions.fetchRoomMembers({ communityId, roomId, boardId });
    }
  }, [communityId, roomId, boardId, actions]);

  if (isLoadingBoard) {
    return <LinearProgress />;
  }

  if (!board) {
    return <Error text={intl.get('board.not_found')} />;
  }

  if (
    board?.id !== boardId ||
    isLoadingCards ||
    isLoadingRoomMembers ||
    roomMembersRequested.communityId !== communityId ||
    roomMembersRequested.roomId !== roomId
  ) {
    return <LinearProgress />;
  }

  if (cardId) {
    return <CardUpdate params={params} />;
  }

  return <CardCreate />;
};

const ConnectedCardContainer = connect(mapStateToProps, mapDispatchToProps)(CardContainer);

export default ConnectedCardContainer;
